"use client";

import { useCallback, useEffect, useState } from "react";
import { X, Settings } from "lucide-react";
import type { ConnectionDetails } from "@client/types/connection.types";
import { useBreakoutMediaBroadcast } from "@client/components/meet/hooks/useBreakoutMediaBroadcast";
import { Spinner } from "@client/components/ui/Spinner";
import { BreakoutRoomsManagerList } from "@client/components/meet/BreakoutRoomsManagerList";
import {
  BreakoutAssignmentModeFieldset,
  dialogButtonPrimary,
  dialogButtonSecondary,
  dialogFieldInput,
  dialogIconButton,
  dialogNumberInput,
  type AssignmentMode
} from "@client/components/meet/breakoutRoomsDialogShared";

interface BreakoutRoomRow {
  id: string;
  name: string;
  themeColor?: string | null;
  socketChannelId?: string | null;
  livekitRoomName: string;
  assignments: Array<{ id: string; userId?: number | null; guestId?: string | null; state: string }>;
}

interface BreakoutParticipant {
  id: string;
  userId?: number | null;
  guestId?: string | null;
  name?: string | null;
  userName: string;
  isGuest: boolean;
  waitingRoomHeld: boolean;
  isMeetingHost?: boolean;
  avatarUrl?: string | null;
}

interface BreakoutData {
  id: string;
  status: "not_started" | "open" | "closing" | "closed";
  openedAt?: string | null;
  closesAt?: string | null;
  rooms: BreakoutRoomRow[];
  participants: BreakoutParticipant[];
  settings?: {
    assignmentMode?: "manual" | "auto" | "self_select";
    allowParticipantMove?: boolean;
    allowReturnToMain?: boolean;
    countdownSeconds?: number;
    autoCloseMinutes?: number;
    sessionEndsAt?: string | null;
  };
}

interface Props {
  roomName: string;
  isOpen: boolean;
  onClose: () => void;
  /** From LiveKit when DB settings omitted sessionEndsAt (legacy) */
  liveSessionEndsAt?: string | null;
  /** Opens the manager when auto-close is due while the dialog was closed */
  onAutoCloseDue?: () => void;
  isReconnecting?: boolean;
  connectionDetails: ConnectionDetails;
  onSwitchRoom: (details: {
    roomName: string;
    participantToken: string;
    chatId?: string | null;
    chatEnabled?: boolean;
    chatEmbedUrl?: string | null;
    breakoutSessionId?: string;
    breakoutRoomId?: string;
    breakoutTitle?: string;
    breakoutThemeColor?: string;
    guestToken?: string | null;
  }) => void;
}

type RoomActivity = {
  id: string;
  participants: Array<{
    identity: string;
    name: string;
    tracks: Array<{ type: number; source: number; muted: boolean }>;
  }>;
};

type LastClosedSessionTemplate = {
  roomCount: number;
  names: string[];
  settings: BreakoutData["settings"];
};

function participantKey(person: BreakoutParticipant): string {
  return person.userId ? String(person.userId) : `guest:${person.guestId}`;
}

function breakoutStatusLabel(status: BreakoutData["status"]): string {
  if (status === "not_started") return "Not started";
  if (status === "open") return "In progress";
  if (status === "closing") return "Closing";
  if (status === "closed") return "Closed";
  return String(status).replaceAll("_", " ");
}

function resolveSessionEndsAt(next: BreakoutData): string | null {
  if (typeof next.settings?.sessionEndsAt === "string") return next.settings.sessionEndsAt;
  if (next.status !== "open") return null;
  const minutes =
    typeof next.settings?.autoCloseMinutes === "number"
      ? Math.max(0, Math.min(240, next.settings.autoCloseMinutes))
      : 0;
  if (minutes <= 0) return null;
  const openedAtRaw = next.openedAt;
  if (typeof openedAtRaw !== "string" || !openedAtRaw.trim()) return null;
  const openedAt = new Date(openedAtRaw);
  if (Number.isNaN(openedAt.getTime())) return null;
  return new Date(openedAt.getTime() + minutes * 60_000).toISOString();
}

export function BreakoutRoomsPanel({
  roomName,
  isOpen,
  onClose,
  liveSessionEndsAt,
  onAutoCloseDue,
  isReconnecting = false,
  connectionDetails,
  onSwitchRoom
}: Props) {
  const [session, setSession] = useState<BreakoutData | null>(null);
  const [roomCount, setRoomCount] = useState(4);
  const [assignments, setAssignments] = useState<Record<string, string>>({});
  const [roomNames, setRoomNames] = useState<Record<string, string>>({});
  const [assignmentMode, setAssignmentMode] = useState<AssignmentMode>("auto");
  const [allowParticipantMove, setAllowParticipantMove] = useState(true);
  const [allowReturnToMain, setAllowReturnToMain] = useState(true);
  const [countdownSeconds, setCountdownSeconds] = useState(10);
  const [autoCloseMinutes, setAutoCloseMinutes] = useState(0);
  const [closeConfirmOpen, setCloseConfirmOpen] = useState(false);
  const [discardConfirmOpen, setDiscardConfirmOpen] = useState(false);
  const [autoClosePromptOpen, setAutoClosePromptOpen] = useState(false);
  const [sessionEndsAt, setSessionEndsAt] = useState<string | null>(null);
  const [nowTick, setNowTick] = useState(() => Date.now());
  const [busy, setBusy] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [meetingParticipantCount, setMeetingParticipantCount] = useState(0);
  const [error, setError] = useState("");
  const [broadcastMessage, setBroadcastMessage] = useState("");
  const [roomActivity, setRoomActivity] = useState<RoomActivity[]>([]);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [collapsedRoomIds, setCollapsedRoomIds] = useState<Record<string, boolean>>({});
  const [renamingRoomId, setRenamingRoomId] = useState<string | null>(null);
  const [lastClosedTemplate, setLastClosedTemplate] = useState<LastClosedSessionTemplate | null>(null);

  const { voiceBroadcastActive, shareBroadcastActive, stopVoiceBroadcast, stopShareBroadcast, stopAllBroadcasts } =
    useBreakoutMediaBroadcast(roomName, connectionDetails.serverUrl);
  const isBreakout = Boolean(connectionDetails.breakoutRoomId);
  const canManageBreakouts = connectionDetails.isHost === true || connectionDetails.isModerator === true;
  const isNonManager = !canManageBreakouts;

  useEffect(() => {
    const sessionId = session?.id;
    const shouldStopMedia = !isOpen || session?.status !== "open";
    if (!shouldStopMedia || !sessionId) return;
    void (async () => {
      try {
        if (voiceBroadcastActive) await stopVoiceBroadcast(sessionId);
        else if (shareBroadcastActive) await stopShareBroadcast(sessionId);
        else await stopAllBroadcasts();
      } catch {
        await stopAllBroadcasts();
      }
    })();
  }, [
    isOpen,
    session?.id,
    session?.status,
    shareBroadcastActive,
    stopAllBroadcasts,
    stopShareBroadcast,
    stopVoiceBroadcast,
    voiceBroadcastActive
  ]);

  const load = useCallback(
    async (options?: { silent?: boolean }) => {
      if (!options?.silent) setIsLoading(true);
      try {
        const response = await fetch(`/api/meeting/${encodeURIComponent(roomName)}/breakouts/session`, {
          cache: "no-store"
        });
        const result = (await response.json()) as {
          status: boolean;
          message?: string;
          data?: BreakoutData | null;
          lastClosedSession?: LastClosedSessionTemplate | null;
          meetingParticipantCount?: number;
        };
        if (!response.ok || !result.status) throw new Error(result.message || "Could not load breakout rooms");
        const next = result.data ?? null;
        setSession(next);
        setLastClosedTemplate(result.lastClosedSession ?? null);
        if (next) {
          setMeetingParticipantCount(
            next.participants.filter((person) => Boolean(person.userId || person.guestId)).length
          );
        } else {
          setMeetingParticipantCount(
            typeof result.meetingParticipantCount === "number" ? Math.max(0, result.meetingParticipantCount) : 0
          );
        }
        if (next?.status === "open" || next?.status === "closing") {
          const statusResponse = await fetch(`/api/meeting/${encodeURIComponent(roomName)}/breakouts/status`, {
            cache: "no-store"
          });
          const statusResult = (await statusResponse.json()) as { status: boolean; data?: { rooms: RoomActivity[] } };
          if (statusResponse.ok && statusResult.status && statusResult.data) setRoomActivity(statusResult.data.rooms);
          else setRoomActivity([]);
        } else setRoomActivity([]);
        if (next) {
          const current: Record<string, string> = {};
          for (const room of next.rooms)
            for (const assignment of room.assignments) {
              if (assignment.userId) current[String(assignment.userId)] = room.id;
              else if (assignment.guestId) current[`guest:${assignment.guestId}`] = room.id;
            }
          setAssignments(current);
          setRoomNames(Object.fromEntries(next.rooms.map((room) => [room.id, room.name])));
          setAssignmentMode(next.settings?.assignmentMode ?? "auto");
          setAllowParticipantMove(next.settings?.allowParticipantMove !== false);
          setAllowReturnToMain(next.settings?.allowReturnToMain !== false);
          setCountdownSeconds(
            typeof next.settings?.countdownSeconds === "number"
              ? Math.max(0, Math.min(60, next.settings.countdownSeconds))
              : 10
          );
          setAutoCloseMinutes(
            typeof next.settings?.autoCloseMinutes === "number"
              ? Math.max(0, Math.min(240, next.settings.autoCloseMinutes))
              : 0
          );
          setSessionEndsAt(resolveSessionEndsAt(next));
        } else {
          setAssignments({});
          setRoomNames({});
          setSessionEndsAt(null);
          setAutoClosePromptOpen(false);
        }
      } finally {
        if (!options?.silent) setIsLoading(false);
      }
    },
    [roomName]
  );

  useEffect(() => {
    if (!isOpen || isNonManager) return;
    setError("");
    void load().catch((cause: unknown) =>
      setError(cause instanceof Error ? cause.message : "Could not load breakout rooms")
    );
  }, [isOpen, isNonManager, roomName, load]);

  useEffect(() => {
    if (!isOpen || isReconnecting || isNonManager) return;
    void load({ silent: true }).catch(() => undefined);
  }, [
    isOpen,
    isReconnecting,
    isNonManager,
    load,
    connectionDetails.participantToken,
    connectionDetails.breakoutRoomId,
    connectionDetails.livekitRoomName
  ]);

  useEffect(() => {
    if (!isOpen) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape" || closeConfirmOpen || autoClosePromptOpen || discardConfirmOpen || busy) return;
      if (session?.status === "not_started") {
        setDiscardConfirmOpen(true);
        return;
      }
      onClose();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [isOpen, onClose, closeConfirmOpen, autoClosePromptOpen, discardConfirmOpen, session?.status, busy]);

  useEffect(() => {
    if (!liveSessionEndsAt) return;
    setSessionEndsAt((prev) => prev ?? liveSessionEndsAt);
  }, [liveSessionEndsAt]);

  useEffect(() => {
    const sessionActive = !session || session.status === "open";
    if (!sessionActive || !sessionEndsAt) return;
    const timer = window.setInterval(() => setNowTick(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, [session?.status, sessionEndsAt]);

  useEffect(() => {
    const sessionActive = !session || session.status === "open";
    if (!sessionActive || !sessionEndsAt || isNonManager) return;
    if (new Date(sessionEndsAt).getTime() <= Date.now()) {
      setAutoClosePromptOpen(true);
      if (!isOpen) onAutoCloseDue?.();
    }
  }, [session?.status, sessionEndsAt, nowTick, isOpen, onAutoCloseDue, isNonManager]);

  useEffect(() => {
    if (!isOpen || (session?.status !== "open" && session?.status !== "closing")) return;
    const timer = window.setInterval(() => {
      void load({ silent: true }).catch(() => undefined);
    }, 5000);
    return () => window.clearInterval(timer);
  }, [isOpen, session?.status, roomName, load]);

  const breakoutSettingsPayload = () => ({
    assignmentMode,
    allowParticipantMove,
    allowReturnToMain,
    countdownSeconds,
    autoCloseMinutes
  });

  const handleAssignmentModeChange = (mode: AssignmentMode) => {
    setAssignmentMode(mode);
    if (!session || session.status !== "not_started" || mode === assignmentMode) return;
    setBusy(true);
    setError("");
    void request("session", "PATCH", {
      sessionId: session.id,
      settings: { ...breakoutSettingsPayload(), assignmentMode: mode },
      assignments: mode === "self_select" ? [] : buildAssignmentRows(assignments)
    })
      .then(() => load({ silent: true }))
      .catch((cause: unknown) => {
        setError(cause instanceof Error ? cause.message : "Could not update assignment method");
        void load({ silent: true }).catch(() => undefined);
      })
      .finally(() => setBusy(false));
  };

  const buildAssignmentRows = (assignmentMap: Record<string, string>) => {
    if (!session) return [];
    return Object.entries(assignmentMap)
      .filter(([, roomId]) => roomId)
      .flatMap(([key, breakoutRoomId]) => {
        const participant = session.participants.find(
          (person) =>
            (person.userId && String(person.userId) === key) || (person.guestId && `guest:${person.guestId}` === key)
        );
        if (!participant) return [];
        const identity = participant.userId ? { userId: participant.userId } : { guestId: participant.guestId! };
        return [
          { ...identity, breakoutRoomId, participantId: participant.id, waitingRoomHeld: participant.waitingRoomHeld }
        ];
      });
  };

  const persistNotStartedAssignments = async (assignmentMap: Record<string, string>) => {
    if (!session || session.status !== "not_started") return;
    try {
      await request("session", "PATCH", {
        sessionId: session.id,
        assignments: buildAssignmentRows(assignmentMap)
      });
      await load({ silent: true });
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Could not update assignments");
    }
  };

  const request = async (path: string, method: string, body: object) => {
    const response = await fetch(`/api/meeting/${encodeURIComponent(roomName)}/breakouts/${path}`, {
      method,
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body)
    });
    const result = (await response.json()) as { status: boolean; message?: string; data?: unknown };
    if (!response.ok || !result.status) throw new Error(result.message || "Breakout room action failed");
    return result.data;
  };

  const fetchSessionSnapshot = async (): Promise<BreakoutData | null> => {
    const response = await fetch(`/api/meeting/${encodeURIComponent(roomName)}/breakouts/session`, {
      cache: "no-store"
    });
    const result = (await response.json()) as { status: boolean; message?: string; data?: BreakoutData | null };
    if (!response.ok || !result.status) throw new Error(result.message || "Could not load breakout session");
    return result.data ?? null;
  };

  const waitForSessionClosed = async (sessionId: string) => {
    for (;;) {
      const snapshot = await fetchSessionSnapshot();
      if (!snapshot) return;
      if (snapshot.status === "closed") return;
      if (snapshot.status === "closing" && snapshot.closesAt) {
        const remainingMs = new Date(snapshot.closesAt).getTime() - Date.now();
        if (remainingMs <= 0) {
          try {
            await request("close", "POST", { sessionId, finalize: true });
          } catch (cause) {
            const retry = await fetchSessionSnapshot();
            if (retry?.status === "closed") return;
            throw cause;
          }
          continue;
        }
        await new Promise((resolve) => window.setTimeout(resolve, Math.min(remainingMs, 1000)));
        continue;
      }
      await new Promise((resolve) => window.setTimeout(resolve, 1000));
    }
  };

  const handleCreate = async (template?: LastClosedSessionTemplate) => {
    setBusy(true);
    setError("");
    try {
      const fromTemplate = template ?? lastClosedTemplate;
      if (fromTemplate) {
        setRoomCount(fromTemplate.roomCount);
        setAssignmentMode(fromTemplate.settings?.assignmentMode ?? "manual");
        setAllowParticipantMove(fromTemplate.settings?.allowParticipantMove !== false);
        setAllowReturnToMain(fromTemplate.settings?.allowReturnToMain !== false);
        setCountdownSeconds(
          typeof fromTemplate.settings?.countdownSeconds === "number" ? fromTemplate.settings.countdownSeconds : 10
        );
        setAutoCloseMinutes(
          typeof fromTemplate.settings?.autoCloseMinutes === "number" ? fromTemplate.settings.autoCloseMinutes : 0
        );
      }
      await request("session", "POST", {
        roomCount: fromTemplate?.roomCount ?? roomCount,
        names: fromTemplate?.names,
        settings: fromTemplate?.settings ?? breakoutSettingsPayload()
      });
      await load();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Could not create rooms");
    } finally {
      setBusy(false);
    }
  };

  const handleOpen = async () => {
    if (!session) return;
    setBusy(true);
    setError("");
    try {
      const rows = buildAssignmentRows(assignments);
      await request("session", "PATCH", {
        sessionId: session.id,
        assignments: assignmentMode === "self_select" ? [] : rows,
        settings: breakoutSettingsPayload(),
        rooms: session.rooms.map((room) => ({ id: room.id, name: roomNames[room.id] || room.name }))
      });
      await request("open", "POST", { sessionId: session.id });
      await load();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Could not open breakout rooms");
    } finally {
      setBusy(false);
    }
  };

  const handleJoin = async (breakoutRoomId: string) => {
    setBusy(true);
    setError("");
    try {
      const data = (await request("join", "POST", { breakoutRoomId })) as {
        roomName: string;
        participantToken: string;
        chatId?: string | null;
        chatEnabled?: boolean;
        chatEmbedUrl?: string | null;
        breakoutSessionId: string;
        breakoutRoomId: string;
        breakoutTitle: string;
        breakoutThemeColor?: string;
      };
      onSwitchRoom(data);
      onClose();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Could not join breakout room");
    } finally {
      setBusy(false);
    }
  };

  const handleAddRoom = async () => {
    if (!session) return;
    setBusy(true);
    setError("");
    try {
      await request("rooms", "POST", { sessionId: session.id });
      await load();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Could not add room");
    } finally {
      setBusy(false);
    }
  };

  const handleRenameRoom = async (roomId: string) => {
    if (!session || !roomNames[roomId]?.trim()) return;
    setBusy(true);
    setError("");
    try {
      await request("rooms", "PATCH", { sessionId: session.id, roomId, name: roomNames[roomId] });
      await load();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Could not rename room");
    } finally {
      setBusy(false);
    }
  };

  const handleDeleteRoom = async (roomId: string) => {
    if (!session) return;
    setBusy(true);
    setError("");
    try {
      await request("rooms", "DELETE", { sessionId: session.id, roomId });
      await load();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Could not delete room");
    } finally {
      setBusy(false);
    }
  };

  const handleAssignParticipant = async (person: BreakoutParticipant, breakoutRoomId: string) => {
    if (!session || person.waitingRoomHeld) return;
    setBusy(true);
    setError("");
    try {
      await request("assign", "POST", {
        sessionId: session.id,
        breakoutRoomId,
        ...(person.userId ? { userId: person.userId } : { guestId: person.guestId })
      });
      await load();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Could not move participant");
    } finally {
      setBusy(false);
    }
  };

  const runCloseBreakouts = async () => {
    if (!session) return;
    setBusy(true);
    setError("");
    setCloseConfirmOpen(false);
    try {
      await request("close", "POST", { sessionId: session.id, countdownSeconds });
      await waitForSessionClosed(session.id);
      if (connectionDetails.breakoutRoomId) {
        const data = (await request("leave", "POST", {})) as {
          roomName: string;
          participantToken: string;
          chatId?: string | null;
          chatEnabled?: boolean;
          chatEmbedUrl?: string | null;
        };
        onSwitchRoom(data);
      }
      await load();
      onClose();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Could not close breakout rooms");
      await load().catch(() => undefined);
    } finally {
      setBusy(false);
    }
  };

  const handleRetryFinalize = async () => {
    if (!session) return;
    setBusy(true);
    setError("");
    try {
      await request("close", "POST", { sessionId: session.id, finalize: true });
      await waitForSessionClosed(session.id);
      if (connectionDetails.breakoutRoomId) {
        const data = (await request("leave", "POST", {})) as Parameters<Props["onSwitchRoom"]>[0];
        onSwitchRoom(data);
      }
      await load();
      onClose();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Could not finish closing breakout rooms");
    } finally {
      setBusy(false);
    }
  };

  const handleRecreateAssignments = async () => {
    if (!session) return;
    setBusy(true);
    setError("");
    try {
      await request("session/recreate", "POST", { sessionId: session.id });
      await load();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Could not recreate assignments");
    } finally {
      setBusy(false);
    }
  };

  const handleRequestClose = () => {
    if (busy) return;
    if (session?.status === "not_started") {
      setDiscardConfirmOpen(true);
      return;
    }
    onClose();
  };

  const runDiscardSetup = async () => {
    if (!session || session.status !== "not_started") {
      setDiscardConfirmOpen(false);
      onClose();
      return;
    }
    setBusy(true);
    setError("");
    try {
      await request("session/discard", "POST", { sessionId: session.id });
      setSession(null);
      setAssignments({});
      setRoomNames({});
      setDiscardConfirmOpen(false);
      onClose();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Could not discard breakout setup");
      setDiscardConfirmOpen(false);
    } finally {
      setBusy(false);
    }
  };

  const handleExtendSessionTimer = async () => {
    if (!session) return;
    setBusy(true);
    setError("");
    try {
      await request("session/extend", "POST", { sessionId: session.id, extendMinutes: 5 });
      setAutoClosePromptOpen(false);
      await load();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Could not extend breakout timer");
    } finally {
      setBusy(false);
    }
  };

  const handleMoveParticipant = (person: BreakoutParticipant, breakoutRoomId: string) => {
    if (!session || person.waitingRoomHeld) return;
    const key = participantKey(person);
    const next = { ...assignments, [key]: breakoutRoomId };
    setAssignments(next);
    if (session.status === "not_started") {
      void persistNotStartedAssignments(next);
      return;
    }
    void handleAssignParticipant(person, breakoutRoomId);
  };

  const handleExchangeParticipants = (person: BreakoutParticipant, other: BreakoutParticipant) => {
    if (!session) return;
    if (session.status === "not_started") {
      const keyA = participantKey(person);
      const keyB = participantKey(other);
      const roomA = assignments[keyA] ?? resolvePersonRoomIdFromState(person, session.rooms, assignments);
      const roomB = assignments[keyB] ?? resolvePersonRoomIdFromState(other, session.rooms, assignments);
      const next = { ...assignments };
      if (roomA) next[keyB] = roomA;
      else delete next[keyB];
      if (roomB) next[keyA] = roomB;
      else delete next[keyA];
      setAssignments(next);
      void persistNotStartedAssignments(next);
      return;
    }
    void runExchangeParticipantsApi(person, other);
  };

  const resolvePersonRoomIdFromState = (
    person: BreakoutParticipant,
    rooms: BreakoutRoomRow[],
    assignmentMap: Record<string, string>
  ): string | undefined => {
    const key = participantKey(person);
    if (assignmentMap[key]) return assignmentMap[key];
    for (const room of rooms) {
      for (const assignment of room.assignments) {
        if (person.userId && assignment.userId === person.userId) return room.id;
        if (person.guestId && assignment.guestId === person.guestId) return room.id;
      }
    }
    return undefined;
  };

  const runExchangeParticipantsApi = async (person: BreakoutParticipant, other: BreakoutParticipant) => {
    if (!session) return;
    setBusy(true);
    setError("");
    try {
      await request("exchange", "POST", {
        sessionId: session.id,
        ...(person.userId ? { userIdA: person.userId } : { guestIdA: person.guestId }),
        ...(other.userId ? { userIdB: other.userId } : { guestIdB: other.guestId })
      });
      await load();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Could not exchange participants");
    } finally {
      setBusy(false);
    }
  };

  const runUnassignParticipantApi = async (person: BreakoutParticipant) => {
    if (!session) return;
    setBusy(true);
    setError("");
    try {
      await request("unassign", "POST", {
        sessionId: session.id,
        ...(person.userId ? { userId: person.userId } : { guestId: person.guestId })
      });
      await load();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Could not unassign participant");
    } finally {
      setBusy(false);
    }
  };

  const handleUnassignParticipant = (person: BreakoutParticipant) => {
    if (!session || person.waitingRoomHeld) return;
    const key = participantKey(person);
    if (session.status === "not_started") {
      const next = { ...assignments };
      delete next[key];
      setAssignments(next);
      void persistNotStartedAssignments(next);
      return;
    }
    void runUnassignParticipantApi(person);
  };

  const eligibleParticipants = session?.participants.filter((person) => Boolean(person.userId || person.guestId)) ?? [];

  if (!isOpen) return null;
  const closeCountdownRemaining =
    session?.status === "closing" && session.closesAt
      ? Math.max(0, Math.ceil((new Date(session.closesAt).getTime() - Date.now()) / 1000))
      : null;
  const assignableParticipantCount = session
    ? session.participants.filter((person) => Boolean(person.userId || person.guestId) && !person.isMeetingHost).length
    : 0;
  const participantsPerRoomHint =
    session && session.rooms.length > 0 ? Math.max(1, Math.ceil(assignableParticipantCount / session.rooms.length)) : 0;
  const sessionTimerRemainingSec =
    session?.status === "open" && sessionEndsAt
      ? Math.max(0, Math.ceil((new Date(sessionEndsAt).getTime() - nowTick) / 1000))
      : null;
  const createParticipantsPerRoomHint =
    meetingParticipantCount > 0 && roomCount > 0 ? Math.max(1, Math.ceil(meetingParticipantCount / roomCount)) : null;
  const toggleRoomCollapsed = (roomId: string) =>
    setCollapsedRoomIds((current) => ({ ...current, [roomId]: !current[roomId] }));
  const dialogSubtitle = !session
    ? "Split this meeting into smaller group rooms."
    : session.status === "not_started"
      ? "Assign people to rooms, then open all rooms when you are ready."
      : session.status === "open"
        ? "Manage rooms and move participants while breakout sessions are active."
        : "Split this meeting into smaller group rooms.";
  return (
    <div
      className="fixed inset-0 z-[100] flex items-end justify-center bg-black/55 p-0 sm:items-center sm:p-4"
      role="presentation"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) handleRequestClose();
      }}
    >
      <section
        className={`flex max-h-[min(90vh,720px)] w-full flex-col overflow-hidden rounded-t-2xl bg-white shadow-2xl sm:rounded-2xl ${
          !session && !isBreakout ? "max-w-lg sm:max-w-xl" : "max-w-2xl"
        }`}
        role="dialog"
        aria-modal="true"
        aria-labelledby="breakout-title"
        aria-describedby="breakout-subtitle"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <header className="flex shrink-0 items-start justify-between gap-4 border-b border-gray-200 px-5 py-4 sm:px-6">
          <div className="min-w-0 pr-2">
            <h2 id="breakout-title" className="text-lg font-semibold text-gray-900 sm:text-xl">
              {session ? `Breakout rooms — ${breakoutStatusLabel(session.status)}` : "Breakout rooms"}
            </h2>
            <p id="breakout-subtitle" className="mt-1 text-sm leading-snug text-gray-600">
              {dialogSubtitle}
            </p>
          </div>
          <button
            type="button"
            className={dialogIconButton}
            onClick={handleRequestClose}
            aria-label="Close breakout rooms"
          >
            <X className="size-5" aria-hidden />
          </button>
        </header>

        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-5 sm:px-6">
          {autoClosePromptOpen && session && (
            <div className="fixed inset-0 z-[110] flex items-center justify-center bg-black/50 p-4" role="presentation">
              <section
                className="w-full max-w-md rounded-2xl bg-white p-6 text-gray-900 shadow-2xl"
                role="alertdialog"
                aria-modal="true"
                aria-labelledby="breakout-auto-close-title"
              >
                <h3 id="breakout-auto-close-title" className="text-lg font-semibold text-gray-900">
                  Breakout time is up
                </h3>
                <p className="mt-2 text-sm text-gray-600">Extend the session or close all breakout rooms now.</p>
                <div className="mt-5 flex flex-wrap justify-end gap-2">
                  <button
                    type="button"
                    className="btn-secondary min-h-10 px-4 py-2 text-sm font-medium text-gray-900"
                    onClick={() => void handleExtendSessionTimer()}
                    disabled={busy}
                  >
                    Extend 5 min
                  </button>
                  <button
                    type="button"
                    className="min-h-10 rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
                    onClick={() => {
                      setAutoClosePromptOpen(false);
                      setCloseConfirmOpen(true);
                    }}
                    disabled={busy}
                  >
                    Close now
                  </button>
                </div>
              </section>
            </div>
          )}

          {discardConfirmOpen && session?.status === "not_started" && (
            <div className="fixed inset-0 z-[110] flex items-center justify-center bg-black/50 p-4" role="presentation">
              <section
                className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl"
                role="alertdialog"
                aria-modal="true"
                aria-labelledby="breakout-discard-title"
                aria-describedby="breakout-discard-desc"
              >
                <h3 id="breakout-discard-title" className="text-lg font-semibold text-gray-900">
                  Discard breakout setup?
                </h3>
                <p id="breakout-discard-desc" className="mt-2 text-sm text-gray-600">
                  Room assignments and names will be deleted. You can create a new breakout plan afterward. Nothing has
                  been opened yet.
                </p>
                <div className="mt-5 flex flex-wrap justify-end gap-2">
                  <button
                    type="button"
                    className={dialogButtonSecondary}
                    disabled={busy}
                    onClick={() => setDiscardConfirmOpen(false)}
                  >
                    Keep editing
                  </button>
                  <button
                    type="button"
                    className="min-h-11 cursor-pointer rounded-lg bg-red-600 px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-50"
                    disabled={busy}
                    onClick={() => void runDiscardSetup()}
                  >
                    Discard setup
                  </button>
                </div>
              </section>
            </div>
          )}

          {closeConfirmOpen && session && (
            <div className="fixed inset-0 z-[110] flex items-center justify-center bg-black/50 p-4" role="presentation">
              <section
                className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl"
                role="alertdialog"
                aria-modal="true"
                aria-labelledby="breakout-close-title"
                aria-describedby="breakout-close-desc"
              >
                <h3 id="breakout-close-title" className="text-lg font-semibold text-gray-900">
                  Close all breakout rooms?
                </h3>
                <p id="breakout-close-desc" className="mt-2 text-sm text-gray-600">
                  Participants will see a {countdownSeconds}-second countdown, then return to the main meeting.
                </p>
                <div className="mt-5 flex flex-wrap justify-end gap-2">
                  <button
                    type="button"
                    className={dialogButtonSecondary}
                    onClick={() => setCloseConfirmOpen(false)}
                    disabled={busy}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="min-h-11 cursor-pointer rounded-lg bg-red-600 px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-2"
                    onClick={() => void runCloseBreakouts()}
                    disabled={busy}
                  >
                    Close all rooms
                  </button>
                </div>
              </section>
            </div>
          )}

          {error && (
            <div role="alert" className="mb-4 rounded-lg bg-red-50 p-3 text-sm text-red-700">
              <p>{error}</p>
              {session?.status === "closing" && error.toLowerCase().includes("retry") && (
                <button
                  type="button"
                  className="mt-2 rounded-lg bg-red-700 px-3 py-1.5 text-xs font-medium text-white disabled:opacity-50"
                  disabled={busy}
                  onClick={() => void handleRetryFinalize()}
                >
                  Retry finish closing
                </button>
              )}
            </div>
          )}
          {!session && !isBreakout && canManageBreakouts && (
            <div className="space-y-6">
              {isLoading ? (
                <div className="flex flex-col items-center justify-center gap-3 py-14" role="status" aria-live="polite">
                  <Spinner size="md" />
                  <p className="text-sm text-gray-600">Loading breakout rooms…</p>
                </div>
              ) : (
                <>
                  {lastClosedTemplate && (
                    <div className="rounded-xl border border-gray-200 bg-gray-50 p-4">
                      <p className="text-sm font-medium text-gray-900">Previous breakout session ended</p>
                      <p className="mt-1 text-sm leading-snug text-gray-600">
                        Start a new round with {lastClosedTemplate.roomCount} rooms using the last configuration.
                      </p>
                      <div className="mt-4">
                        <button
                          type="button"
                          className={dialogButtonSecondary}
                          disabled={busy}
                          onClick={() => void handleCreate(lastClosedTemplate)}
                        >
                          Duplicate last setup
                        </button>
                      </div>
                    </div>
                  )}
                  <form
                    className="space-y-6"
                    onSubmit={(event) => {
                      event.preventDefault();
                      void handleCreate();
                    }}
                  >
                    <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-base text-gray-900">
                      <span id="breakout-create-label">Create</span>
                      <label className="sr-only" htmlFor="breakout-room-count">
                        Number of breakout rooms
                      </label>
                      <input
                        id="breakout-room-count"
                        className={dialogNumberInput}
                        type="number"
                        min={1}
                        max={50}
                        inputMode="numeric"
                        aria-labelledby="breakout-create-label"
                        aria-describedby="breakout-create-hint"
                        value={roomCount}
                        disabled={busy}
                        onChange={(event) => setRoomCount(Math.max(1, Math.min(50, Number(event.target.value) || 1)))}
                      />
                      <span>{roomCount === 1 ? "breakout room" : "breakout rooms"}</span>
                    </div>

                    <BreakoutAssignmentModeFieldset
                      name="breakout-assignment-create"
                      value={assignmentMode}
                      onChange={setAssignmentMode}
                      disabled={busy}
                    />

                    <div className="flex flex-col-reverse gap-3 border-t border-gray-200 pt-4 sm:flex-row sm:items-center sm:justify-between">
                      <p id="breakout-create-hint" className="text-sm italic text-gray-600">
                        {createParticipantsPerRoomHint !== null ? (
                          <>
                            *{createParticipantsPerRoomHint}{" "}
                            {createParticipantsPerRoomHint === 1 ? "participant" : "participants"} per room*
                          </>
                        ) : (
                          <>*People in the meeting will be split when breakout rooms open*</>
                        )}
                      </p>
                      <button type="submit" className={dialogButtonPrimary} disabled={busy}>
                        {busy ? "Creating…" : "Create"}
                      </button>
                    </div>
                  </form>
                </>
              )}
            </div>
          )}

          {!session && !isBreakout && !canManageBreakouts && (
            <div className="rounded-xl border border-gray-200 bg-gray-50 p-4 text-sm text-gray-700">
              Breakout rooms are managed by the meeting host.
            </div>
          )}

          {isBreakout && !canManageBreakouts && (
            <div className="rounded-xl border border-gray-200 bg-gray-50 p-4">
              <p className="font-medium text-gray-900">
                You are in {connectionDetails.breakoutTitle || "a breakout room"}.
              </p>
              <button
                type="button"
                className={`mt-4 ${dialogButtonSecondary}`}
                onClick={() =>
                  void request(
                    "leave",
                    "POST",
                    connectionDetails.isGuest && connectionDetails.guestId ? { guestId: connectionDetails.guestId } : {}
                  )
                    .then((data) => {
                      onSwitchRoom(data as Parameters<Props["onSwitchRoom"]>[0]);
                      onClose();
                    })
                    .catch((cause: unknown) =>
                      setError(cause instanceof Error ? cause.message : "Could not return to the main meeting")
                    )
                }
              >
                Return to main meeting
              </button>
            </div>
          )}

          {(!isBreakout || canManageBreakouts) && session && (
            <>
              {session.status === "open" && (
                <>
                  <section
                    className="mb-4 rounded-xl border border-gray-200 bg-gray-50/80 p-4"
                    aria-labelledby="breakout-broadcast-heading"
                  >
                    <h3 id="breakout-broadcast-heading" className="text-sm font-semibold text-gray-900">
                      Broadcast to breakout rooms
                    </h3>
                    <form
                      className="mt-3 flex flex-col gap-2 sm:flex-row sm:items-stretch"
                      onSubmit={(event) => {
                        event.preventDefault();
                        void request("broadcast", "POST", {
                          sessionId: session.id,
                          type: "message",
                          message: broadcastMessage
                        })
                          .then(() => setBroadcastMessage(""))
                          .catch((cause: unknown) =>
                            setError(cause instanceof Error ? cause.message : "Broadcast failed")
                          );
                      }}
                    >
                      <input
                        aria-label="Message to all breakout rooms"
                        className={`min-w-0 flex-1 ${dialogFieldInput}`}
                        maxLength={500}
                        value={broadcastMessage}
                        onChange={(event) => setBroadcastMessage(event.target.value)}
                        placeholder="Message all breakout rooms"
                        disabled={busy}
                      />
                      <button
                        type="submit"
                        disabled={busy || !broadcastMessage.trim()}
                        className={`shrink-0 sm:min-w-[7.5rem] ${dialogButtonSecondary}`}
                      >
                        Broadcast
                      </button>
                    </form>
                  </section>
                </>
              )}
              <BreakoutRoomsManagerList
                sessionStatus={session.status}
                rooms={session.rooms}
                roomNames={roomNames}
                assignments={assignments}
                assignmentMode={assignmentMode}
                eligibleParticipants={eligibleParticipants}
                roomActivity={roomActivity}
                busy={busy}
                settingsOpen={settingsOpen}
                countdownSeconds={countdownSeconds}
                autoCloseMinutes={autoCloseMinutes}
                allowReturnToMain={allowReturnToMain}
                allowParticipantMove={allowParticipantMove}
                sessionEndsAt={sessionEndsAt}
                collapsedRoomIds={collapsedRoomIds}
                renamingRoomId={renamingRoomId}
                closeCountdownRemaining={closeCountdownRemaining}
                sessionTimerRemainingSec={sessionTimerRemainingSec}
                participantsPerRoomHint={participantsPerRoomHint}
                assignableParticipantCount={assignableParticipantCount}
                onSetRoomNames={setRoomNames}
                onSetAssignmentMode={handleAssignmentModeChange}
                onSetCountdownSeconds={setCountdownSeconds}
                onSetAutoCloseMinutes={setAutoCloseMinutes}
                onSetAllowReturnToMain={setAllowReturnToMain}
                onSetAllowParticipantMove={setAllowParticipantMove}
                onToggleRoomCollapsed={toggleRoomCollapsed}
                onSetRenamingRoomId={setRenamingRoomId}
                onDeleteRoom={(roomId) => void handleDeleteRoom(roomId)}
                onRenameRoom={(roomId) => void handleRenameRoom(roomId)}
                onJoinRoom={(roomId) => void handleJoin(roomId)}
                onMoveParticipant={handleMoveParticipant}
                onExchangeParticipants={handleExchangeParticipants}
                onUnassignParticipant={handleUnassignParticipant}
                onExtendSessionTimer={() => void handleExtendSessionTimer()}
              />
              {(session.status === "not_started" || session.status === "open") && session.rooms.length >= 50 && (
                <p className="mt-3 text-xs text-amber-800">Maximum of 50 breakout rooms per session.</p>
              )}
            </>
          )}
        </div>
        {(!isBreakout || canManageBreakouts) &&
          session &&
          (session.status === "not_started" || session.status === "open" || session.status === "closing") && (
            <footer className="flex shrink-0 flex-col gap-3 border-t border-gray-200 bg-gray-50 px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
              {(session.status === "not_started" || session.status === "open") && (
                <div className="flex flex-wrap items-center gap-2">
                  <button
                    type="button"
                    className={`inline-flex items-center justify-center gap-2 ${dialogButtonSecondary}`}
                    aria-expanded={settingsOpen}
                    aria-controls="breakout-settings-panel"
                    onClick={() => setSettingsOpen((open) => !open)}
                  >
                    <Settings className="size-4 shrink-0" aria-hidden />
                    Settings
                  </button>
                  {session.status === "not_started" && (
                    <button
                      type="button"
                      className="min-h-11 cursor-pointer rounded-lg border border-red-200 bg-white px-3 py-2 text-sm font-medium text-red-700 transition-colors hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-50"
                      disabled={busy}
                      onClick={() => setDiscardConfirmOpen(true)}
                    >
                      Discard setup
                    </button>
                  )}
                </div>
              )}
              <div className="flex flex-wrap gap-2 sm:ml-auto sm:justify-end">
                {session.status === "not_started" && (
                  <>
                    {(assignmentMode === "auto" || assignmentMode === "manual") && (
                      <button
                        type="button"
                        className={dialogButtonSecondary}
                        disabled={busy}
                        onClick={() => void handleRecreateAssignments()}
                      >
                        Recreate
                      </button>
                    )}
                    <button
                      type="button"
                      className={dialogButtonSecondary}
                      disabled={busy || session.rooms.length >= 50}
                      onClick={() => void handleAddRoom()}
                    >
                      Add room
                    </button>
                    <button
                      type="button"
                      className={dialogButtonPrimary}
                      disabled={busy}
                      onClick={() => void handleOpen()}
                    >
                      Open all rooms
                    </button>
                  </>
                )}
                {session.status === "open" && (
                  <>
                    <button
                      type="button"
                      className={dialogButtonSecondary}
                      disabled={busy || session.rooms.length >= 50}
                      onClick={() => void handleAddRoom()}
                    >
                      Add room
                    </button>
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() => setCloseConfirmOpen(true)}
                      className={`min-h-11 cursor-pointer rounded-lg bg-red-600 px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-2`}
                    >
                      Close all rooms
                    </button>
                  </>
                )}
                {session.status === "closing" && (
                  <button
                    type="button"
                    className={dialogButtonPrimary}
                    disabled={busy}
                    onClick={() => void handleRetryFinalize()}
                  >
                    Finish closing
                  </button>
                )}
              </div>
            </footer>
          )}
      </section>
    </div>
  );
}
